import RAPIER from '@dimforge/rapier3d-compat'

import type { ObjectBodyRegistry } from './objectBodyRegistry'
import { GROUND_TOUCH_MARGIN } from './physicsConstants'
import type { PreviewWorld } from '../types'

/**
 * Touched イベント検知のサブシステム。
 *
 * createAvatarController から「アバターとオブジェクトの接触検知」を切り出した
 * もの。前フレームの接触集合（prevTouched）はファクトリ内部に隠蔽し、本体には
 * 検知（detectTouches / detectSeatedTouches）と履歴クリア（clear。reset 用）
 * だけを公開する。enter（新規接触）の瞬間にのみ onTouch を呼ぶ。
 */
export interface TouchDetection {
  /** 歩行中の接触検知（solid 衝突・足元レイ・sensor 重なりの 3 系統）。 */
  detectTouches(): void
  /** 乗車中の接触検知（sensor 重なりのみ。車両自身の部品は除外）。 */
  detectSeatedTouches(vehiclePartIds: ReadonlySet<string>): void
  /** 接触履歴を空にする（reset 用。次の検知はすべて enter として発火する）。 */
  clear(): void
}

/**
 * Touched 検知サブシステムを生成する。
 *
 * @param deps.physicsWorld rapier の物理 World（sensor 重なりの列挙元）。
 * @param deps.characterController アバターの KCC（solid 衝突の列挙元）。
 * @param deps.world 見た目・状態の真実源。CanTouch の実行時値を参照する。
 * @param deps.registry collider handle → objectId / 適用モードの逆引き。
 * @param deps.getAvatarCollider アバターのコライダー。本体所有の参照へ構築順に依存せずアクセスするため getter で受け取る。
 * @param deps.castGroundRay 足元の接地相手を返すレイキャスト（physicsController が所有）。
 * @param deps.getPushedObjectIds このステップでプログラム移動がアバターを押した部品の
 *   id 集合（physicsController が所有）。押している部品は KCC の障害物から除外され
 *   computedCollision に載らないため、接触として補完する。
 * @param deps.onTouch アバターが新たにオブジェクトへ接触した瞬間に objectId で呼ばれる。
 */
export const createTouchDetection = (deps: {
  physicsWorld: RAPIER.World
  characterController: RAPIER.KinematicCharacterController
  world: PreviewWorld
  registry: Pick<ObjectBodyRegistry, 'objectIdOf' | 'modeOf'>
  getAvatarCollider: () => RAPIER.Collider
  castGroundRay: (margin: number) => RAPIER.Collider | null
  getPushedObjectIds: () => ReadonlySet<string>
  onTouch: (objectId: string) => void
}): TouchDetection => {
  const {
    physicsWorld,
    characterController,
    world,
    registry,
    getAvatarCollider,
    castGroundRay,
    getPushedObjectIds,
    onTouch,
  } = deps
  // 前フレームの接触集合。enter（新規接触）判定の基準。
  let prevTouched = new Set<string>()

  /**
   * 今フレームの接触集合を作り、enter（新規接触）のみ onTouch を呼ぶ。
   *
   * 3 系統で検知する（Roblox の Touched 挙動に合わせる）:
   * - solid: KCC が移動中にぶつかった衝突（computedCollision）。CanTouch=false の
   *   部品は Touched を発火させないので除外する。
   * - 足元: computedCollision は水平移動の衝突しか返さず、平らな床に立っている・
   *   歩いている状態を拾えない（接地維持は snapToGround 任せで下方向の押し込みを
   *   0 にしているため）。Roblox では部品に乗るだけで Touched が発火するので、
   *   足元へ短い下向きレイを撃って接地相手を補完する。
   * - sensor: CanCollide=false かつ CanTouch=true の部品はアバターをすり抜けるが、
   *   重なり（intersectionPairsWith）で接触として検知する（トリガーゾーン）。
   */
  const detectTouches = (): void => {
    const touched = new Set<string>()
    // CanTouch は state を真実源にする（setCanTouch の実行時変更に追従する）。
    const states = world.getState()
    const canTouchOf = (objectId: string): boolean =>
      states.get(objectId)?.canTouch !== false
    const count = characterController.numComputedCollisions()
    for (let i = 0; i < count; i += 1) {
      const collision = characterController.computedCollision(i)
      const collider = collision?.collider
      if (!collider) {
        continue
      }
      const objectId = registry.objectIdOf(collider.handle)
      // CanTouch=false の solid 部品はぶつかっても Touched を発火させない。
      if (objectId !== undefined && canTouchOf(objectId)) {
        touched.add(objectId)
      }
    }
    // 足元の接地相手（乗っている床）を下向きレイで検知する（castGroundRay 参照）。
    const groundCollider = castGroundRay(GROUND_TOUCH_MARGIN)
    if (groundCollider) {
      const objectId = registry.objectIdOf(groundCollider.handle)
      if (objectId !== undefined && canTouchOf(objectId)) {
        touched.add(objectId)
      }
    }
    // プログラム移動でアバターを押している部品は KCC の障害物から除外して
    // いるため computedCollision に載らない。押している＝接触しているので
    // ここで Touched に数える（実機もパーツが Humanoid を押す間 Touched が出る）。
    for (const id of getPushedObjectIds()) {
      if (canTouchOf(id)) {
        touched.add(id)
      }
    }
    // すり抜ける sensor 部品との重なりを検知（アバターは solid なので、相手が
    // sensor のペアだけが intersectionPairsWith に列挙される）。
    // 「sensor かつ CanTouch」のみ採用する明示フィルタも掛け、将来のモード遷移
    // バグや active pair 拡大が Touched の誤発火に化けないよう防御する。
    physicsWorld.intersectionPairsWith(getAvatarCollider(), (other) => {
      const objectId = registry.objectIdOf(other.handle)
      if (
        objectId !== undefined &&
        registry.modeOf(objectId) === 'sensor' &&
        canTouchOf(objectId)
      ) {
        touched.add(objectId)
      }
    })
    for (const id of touched) {
      if (!prevTouched.has(id)) {
        onTouch(id)
      }
    }
    prevTouched = touched
  }

  /**
   * 乗車中の接触検知。アバターは座席へ kinematic 同期されるだけで KCC は
   * 動かないため、solid 衝突・足元レイ（歩行用）は対象外とし、sensor
   * （CanCollide=false かつ CanTouch=true のトリガーゾーン）との重なりのみ
   * 検知する。車両自身の部品（装飾の非衝突パーツ等）は接触に数えない。
   */
  const detectSeatedTouches = (vehiclePartIds: ReadonlySet<string>): void => {
    const touched = new Set<string>()
    const states = world.getState()
    physicsWorld.intersectionPairsWith(getAvatarCollider(), (other) => {
      const objectId = registry.objectIdOf(other.handle)
      if (
        objectId !== undefined &&
        !vehiclePartIds.has(objectId) &&
        registry.modeOf(objectId) === 'sensor' &&
        states.get(objectId)?.canTouch !== false
      ) {
        touched.add(objectId)
      }
    })
    for (const id of touched) {
      if (!prevTouched.has(id)) {
        onTouch(id)
      }
    }
    prevTouched = touched
  }

  const clear = (): void => {
    prevTouched = new Set<string>()
  }

  return { detectTouches, detectSeatedTouches, clear }
}
